import { useCallback, useMemo, useState } from 'react';
import { useUpdateEffect } from 'ahooks';
import { SearchIndexes } from '@rocco/types/enum/search-indexes';
import type {
    FilterStates,
    FilterStatesKey,
} from '@rocco/ui/filter-sort/view-models/filter-states';
import {
    isFacetFilterState,
    type FilterState,
} from '@rocco/ui/filter-sort/view-models/filter-state/filter-state';
import type {
    FiltersConfig,
    FiltersConfigSection,
    ModalFiltersConfigSection,
} from '@rocco/ui/filter-sort/types/filter-config-section';
import type { SortOption } from '@rocco/types/enum/sort-option';
import type { AggregationType } from '@rocco/types/aggregation-type';
import type { MultipleValuesFilterState } from '@rocco/ui/filter-sort/view-models/filter-state/multiple-values-filter-state';
import type { SingleValueFilterState } from '@rocco/ui/filter-sort/view-models/filter-state/single-value-filter-state';
import type { SearchableListFilterState } from '@rocco/ui/filter-sort/view-models/filter-state/searchable-list-filter-state';
import type { DetailedPlace } from '@archipro-website/web/app/modules/location/type';
import type { LocationPlace } from '@rocco/ui/location/types/location-place';

interface BaseAggregationArgs {
    aggregateType: AggregationType;
    onSuccess: (data: FilterStates) => void;
    onStart: () => void;
    onFinish: () => void;
}

interface SearchableListAggregationArgs extends BaseAggregationArgs {
    aggregateType:
        | 'searchListLoadMore'
        | 'searchListSearch'
        | 'groupAlphabetical';
    aggregateFilter: string;
    filterStates?: FilterStates;
    aggregationsParams: {
        [key: string]: {
            search?: string;
            after?: Record<string, unknown>;
            size?: number;
            startsWith?: string;
        };
    };
}

interface FilterAggregationArgs extends BaseAggregationArgs {
    aggregateType: 'filterAggregation';
    filterStates: FilterStates;
    /**
     * How much of the aggregation suite the caller consumes. 'count' skips
     * the facet/range aggregations and only computes the result count — the
     * right scope when nothing but totalCount is read from the response.
     * Defaults to the full suite.
     */
    scope?: 'count' | 'full';
}

export const isSearchableListAggregation = (
    args: BaseAggregationArgs,
): args is SearchableListAggregationArgs => {
    return (
        args.aggregateType === 'searchListLoadMore' ||
        args.aggregateType === 'searchListSearch' ||
        args.aggregateType === 'groupAlphabetical'
    );
};

export const isFilterAggregation = (
    args: BaseAggregationArgs,
): args is FilterAggregationArgs => {
    return args.aggregateType === 'filterAggregation';
};

export type AggregationArgs =
    | SearchableListAggregationArgs
    | FilterAggregationArgs;

export interface LocationServiceOptions {
    country: 'NZ' | 'AU' | 'US';
    currentLocation: {
        isLoading: boolean;
        location: LocationPlace | null;
        onCurrentLocationFetch: () => void;
    };
    keywordLocations: {
        isLoading: boolean;
        locations: LocationPlace[] | null;
        onKeywordLocationsFetch: (keyword: string, limit: number) => void;
        onKeywordResetPlaces: () => void;
    };
}

interface UseSearchFilterHookOptions {
    config: FiltersConfig;
    searchIndex?: SearchIndexes | null;
    initialStates: FilterStates;
    isCategoryPage?: boolean;
    locationService: LocationServiceOptions;
    hasAdminPermission?: boolean;
    onRefetch: (filterStates: FilterStates) => void;
    onAggregate: (args: AggregationArgs) => void;
    onAggregateQueueClear: () => void;
    onToggleMultipleValues: (
        state: MultipleValuesFilterState,
        item: SingleValueFilterState,
        checked: boolean,
    ) => MultipleValuesFilterState | SearchableListFilterState;
    onToggleSearchableList: (
        state: SearchableListFilterState,
        item: SingleValueFilterState,
        checked: boolean,
    ) => SearchableListFilterState;
    onFetchLocationByQuery?: (query: string) => DetailedPlace[] | null;
    onFetchCurrentLocation?: () => DetailedPlace | null;
}

export const useSearchFilters = ({
    config,
    searchIndex,
    initialStates,
    isCategoryPage,
    hasAdminPermission,
    onRefetch,
    onAggregate,
    onAggregateQueueClear,
    onToggleMultipleValues,
    onToggleSearchableList,
    onFetchLocationByQuery,
    onFetchCurrentLocation,
    locationService,
}: UseSearchFilterHookOptions) => {
    const [filterStates, setFilterStates] =
        useState<FilterStates>(initialStates);

    // Update filter states when initial states change, skipping the first render
    useUpdateEffect(() => {
        setFilterStates(initialStates);
    }, [initialStates]);

    const getUpdatedFilterStates = useCallback(
        (
            currentFilterStates: FilterStates,
            filterKey: FilterStatesKey,
            state: FilterState,
            isFacetFilter: boolean = false,
        ): FilterStates => {
            if (isFacetFilter) {
                const facetState =
                    currentFilterStates.facet?.facetOptions?.find(
                        (facet) => facet.id === filterKey,
                    );
                if (!facetState || !isFacetFilterState(state)) {
                    return currentFilterStates;
                }
                const newFacetState = { ...facetState.state, ...state };
                return {
                    ...currentFilterStates,
                    facet: {
                        facetOptions:
                            currentFilterStates.facet!.facetOptions!.map(
                                (facet) =>
                                    facet.id === filterKey
                                        ? { ...facet, state: newFacetState }
                                        : facet,
                            ),
                    },
                };
            }

            // Determine if user preferences should be updated based on specific conditions:
            // 1. The filter being changed is 'isPurchasable'
            // 2. The current search index is 'product'
            // 3. We are on a category page (isCategoryPage is truthy)
            // If all conditions are met, set shouldUpdateUserPreferences to true
            // On the old `web` these settings contain "Shop" filter for products section
            const updateUserPrefs =
                filterKey === 'isPurchasable' &&
                searchIndex === SearchIndexes.Products &&
                !!isCategoryPage
                    ? { shouldUpdateUserPreferences: true }
                    : {};

            return {
                ...currentFilterStates,
                ...updateUserPrefs,
                states: {
                    ...currentFilterStates.states,
                    [filterKey]: state,
                },
            };
        },
        [isCategoryPage, searchIndex],
    );

    const resetFilters = useCallback(
        (states?: FilterStates) => {
            const newStates = states ?? {
                visibilityType: initialStates.visibilityType,
                states: {},
            };
            setFilterStates(newStates);
            onRefetch(newStates);
        },
        [initialStates.visibilityType, onRefetch],
    );

    const updateFilter = useCallback(
        (newStates: FilterStates, refetch = true) => {
            setFilterStates(newStates);

            if (refetch) {
                onRefetch(newStates);
            }
        },
        [onRefetch],
    );

    const updateSort = useCallback(
        (sortBy?: SortOption, refetch = true) => {
            setFilterStates((preStates) => {
                if (preStates.sortBy === sortBy) return preStates;

                const newFilterStates = { ...preStates, sortBy };

                if (refetch) {
                    onRefetch(newFilterStates);
                }

                return newFilterStates;
            });
        },
        [onRefetch],
    );

    const _filterOutAdminOnlyFilters = useCallback<
        <T extends FiltersConfigSection | ModalFiltersConfigSection>(
            sections: T[],
        ) => T[]
    >(
        (sections) => {
            return sections.filter(
                (section) => hasAdminPermission || !section.adminOnly,
            );
        },
        [hasAdminPermission],
    );

    /** Memoize config to prevent unnecessary recalculations */
    const memoizedConfig = useMemo(
        () => ({
            ...config,
            barFilters: _filterOutAdminOnlyFilters(config.barFilters),
            modalFilters: _filterOutAdminOnlyFilters(config.modalFilters),
        }),
        [config, _filterOutAdminOnlyFilters],
    );

    /** Memoize return value to prevent unnecessary re-renders of consuming components */
    return useMemo(
        () => ({
            config: memoizedConfig,
            filterStates,
            updateFilter,
            resetFilters,
            updateSort,
            handleAggregate: onAggregate,
            handleAggregateQueueClear: onAggregateQueueClear,
            handleToggleMultipleValues: onToggleMultipleValues,
            handleToggleSearchableList: onToggleSearchableList,
            onFetchLocationByQuery: onFetchLocationByQuery,
            onFetchCurrentLocation: onFetchCurrentLocation,
            getUpdatedFilterStates,
            locationService,
        }),
        [
            memoizedConfig,
            filterStates,
            updateFilter,
            resetFilters,
            updateSort,
            onAggregate,
            onAggregateQueueClear,
            onToggleMultipleValues,
            onToggleSearchableList,
            onFetchLocationByQuery,
            onFetchCurrentLocation,
            getUpdatedFilterStates,
            locationService,
        ],
    );
};
